export interface ChannexProvisioningResult {
  bindingId: string;
  generation: number;
  targetVersion: number;
}

export interface ChannexIframeSession {
  iframeUrl: string;
  expiresInSeconds: number;
}

/**
 * Channex Resource — wird via System.Text.Json + JsonPropertyName-Attributes
 * mit snake_case serialisiert (Channex-Format). Wir akzeptieren beide Casings
 * defensiv, weil in der Praxis je nach ASP.NET-Variante mal so, mal so rauskommt.
 */
export interface ChannexChannelResource {
  id: string;
  type: string;
  attributes: {
    title: string;
    channel: string;
    currency: string;
    isActive?: boolean;
    is_active?: boolean;
    properties?: string[];
    /** OTA-spezifische Settings, z.B. { hotel_id: "12217304" } fuer Booking.com. */
    settings?: Record<string, unknown> | null;
    insertedAt?: string | null;
    inserted_at?: string | null;
    updatedAt?: string | null;
    updated_at?: string | null;
  };
}

/** Lese is_active defensiv aus beiden Casings. */
export function channelIsActive(c: ChannexChannelResource): boolean {
  return c.attributes.isActive ?? c.attributes.is_active ?? false;
}

/** Lese updated_at defensiv. */
export function channelUpdatedAt(c: ChannexChannelResource): string | null {
  return c.attributes.updatedAt ?? c.attributes.updated_at ?? null;
}

/**
 * Lese OTA-Identifier aus settings, falls vorhanden.
 *
 * Achtung: Nicht jede OTA hat eine flache "Listing-ID":
 *   - Booking.com: settings.hotel_id (numerisch, eindeutig)
 *   - Vrbo: settings.property_id (manchmal)
 *   - Airbnb: KEINE flache ID — OAuth-basiert, Listings werden im Channex-Mapping
 *     Iframe verknuepft. settings enthaelt account_id, nicht Listing-IDs.
 *
 * Wenn null zurueckkommt heisst das nicht "Fehler" sondern "diese OTA hat das
 * Konzept nicht". UI sollte das transparent kommunizieren.
 */
export function channelExternalId(c: ChannexChannelResource): string | null {
  const settings = c.attributes.settings;
  if (!settings || typeof settings !== 'object') return null;
  const candidates = ['hotel_id', 'hotelId', 'property_id', 'listing_id', 'room_id'];
  for (const key of candidates) {
    const v = (settings as Record<string, unknown>)[key];
    if (typeof v === 'string' && v.length > 0) return v;
    if (typeof v === 'number') return String(v);
  }
  return null;
}

/**
 * Gibt true zurueck wenn die OTA konzeptionell eine flache externe Listing-ID hat,
 * die wir anzeigen sollten. False fuer OAuth-basierte OTAs (Airbnb).
 */
export function channelSupportsExternalId(channel: string): boolean {
  const lower = (channel ?? '').toLowerCase().replace(/[.\s-]/g, '');
  return lower !== 'airbnb' && lower !== 'airbnbcom';
}

export interface ChannexConnectionInstance {
  id: string;
  title: string;
  isActive: boolean;
  currency: string;
}

export interface ChannexConnectionItem {
  channel: string;
  displayName: string;
  connectMode: 'oauth' | 'hotel_id' | string;
  connectedCount: number;
  connections: ChannexConnectionInstance[];
}

export interface ChannexConnectionsResponse {
  items: ChannexConnectionItem[];
}

export type ChannexWebhookStatus = 'received' | 'processed' | 'ignored' | 'failed' | 'parked';

export interface ChannexWebhookEventResponse {
  id: string;
  eventType: string;
  channexPropertyId?: string | null;
  liveFeedEventId: string;
  status: ChannexWebhookStatus | string;
  receivedAt: string;
  processedAt?: string | null;
  attempts: number;
  error?: string | null;
  payloadHash: string;
  rawJson: string;
}
